import type { RefObject, ReactNode } from 'react';
import {
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo,
  startTransition,
} from 'react';
import { useTracking } from 'react-tracking';
import { Textfield, useIsMounted } from '@simplywallst/ui-core';
import SearchFieldPostfix from '@/components/SearchFieldPostfix';
import { useClickOutside } from '@/hooks/useClickOutside';
import Popper from '@/components/Tooltip/components/Popper';
import { useKeys } from '@/hooks/useKeys';
import { useTranslation } from '@/hooks/useTranslation';
import SearchIcon from '@simplywallst/ui-core/icons/SearchSolid';
import { cn } from '@simplywallst/ui-core/next';

export interface Props {
  light?: boolean;
  name?: string;
  value?: string;
  error?: string;
  disabled?: boolean;
  placeholder?: string;
  defaultState?: ReactNode;
  children: JSX.Element[];
  onFocus?: () => void;
  /** */
  status?: 'success' | 'pending' | 'error';
  onChange?: (value: string) => void;
  onOpen?: (open: boolean, ref: RefObject<HTMLInputElement>) => void;
  onClose?: () => void;
  onReset?: () => void;
  onBlur?: (value: string) => void;
  isFetching?: boolean;
}

export const DesktopSearch = ({
  light,
  name = 'search-search-field',
  value,
  error = '',
  disabled,
  placeholder,
  defaultState,
  children,
  onFocus,
  /** */
  status,
  onChange,
  onOpen,
  onClose,
  onReset,
  onBlur,
  isFetching,
}: Props) => {
  const { trackEvent } = useTracking();
  const t = useTranslation();

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const childRef = useRef<HTMLDivElement>(null);
  const animationDuration = 300;

  const [searchValue, setSearchValue] = useState(value || '');
  const [showSearchResults, setShowSearchResults] = useState(false);

  const handleClose = () => {
    onClose?.();
    onBlur?.(searchValue);
  };
  const handleBlur = useCallback(() => {
    if (showSearchResults) {
      setShowSearchResults(false);
    }
  }, [showSearchResults]);

  useClickOutside([containerRef, childRef], () => {
    handleBlur();
    trackEvent({ action: 'blur', query: searchValue });
    handleClose();
  });

  /** stopPropagation is being derpy so this variable exist */
  const supressFocusEvent = useRef(false);

  useEffect(() => {
    if (typeof onOpen === 'function') {
      onOpen(showSearchResults, inputRef);
    }
  }, [showSearchResults, onOpen, inputRef]);

  const isMounted = useIsMounted();

  useEffect(() => {
    if (typeof onChange === 'function' && isMounted) {
      onChange(searchValue);
    }
  }, [searchValue, onChange]);

  useEffect(() => {
    if (value) {
      setSearchValue(value);
    }
  }, [value]);

  const keys = useMemo(() => ['Tab', 'Enter'] as const, []);
  useKeys({
    targetKeys: keys,
    targetRef: inputRef.current,
    callback: handleBlur,
  });

  const handleReset = () => {
    const { current } = inputRef;
    if (current) current.focus();
    setSearchValue('');
    supressFocusEvent.current = false;
    onReset?.();
    trackEvent({ action: 'reset' });
  };

  const handleFocus = () => {
    startTransition(() => {
      setShowSearchResults(true);
    });
    onFocus?.();
  };

  const isShownDefaultState =
    searchValue === '' || (children.length === 0 && !isFetching);

  const listing = useMemo(
    () =>
      isShownDefaultState ? (
        defaultState
      ) : (
        <ul data-cy-id="search-results-list">{children}</ul>
      ),
    [children, defaultState]
  );

  const isEmpty =
    (children.length === 0 || searchValue === '') &&
    typeof defaultState === 'undefined';

  return (
    <div className="relative z-1 w-full" ref={containerRef}>
      <Textfield
        styleType={light ? 'standardReverse' : 'standard'}
        ref={inputRef}
        name={name}
        type="search"
        disabled={disabled}
        error={error}
        value={searchValue}
        placeholder={placeholder ?? t('app.topbar.nav.search.placeholder')}
        prefix={<SearchIcon className="[&>path]:!fill-brand-01" />}
        postfix={
          <SearchFieldPostfix
            styleType={light ? 'light' : 'dark'}
            onReset={handleReset}
            onFocus={() => {
              supressFocusEvent.current = true;
            }}
            searchQuery={searchValue}
            searchStatus={status}
            isFetching={isFetching}
          />
        }
        onChange={(event) => {
          setSearchValue(event.currentTarget.value);
        }}
        onFocus={() => {
          if (!supressFocusEvent.current) {
            /** only track focus here to avoid double focus event */
            trackEvent({
              action: 'focus',
            });
          }
          handleFocus();
          inputRef.current?.select();
        }}
        autoComplete="off"
        onBlur={handleClose}
      />
      <Popper
        anchorEl={inputRef.current}
        offsetX={-49} // 49 is the width of postfix with the border
        open={showSearchResults}
        placement="bottom-start"
        role="listbox"
        wrapper={(child) => child}
        blockPointerEvents={false}
        zIndex={99999}
        animationDuration={animationDuration}
      >
        <div
          ref={childRef}
          onClick={handleBlur}
          className="w-[492px] overflow-hidden"
        >
          {isEmpty ? null : (
            <div
              className={cn(
                'scrollbar', // Custom scrollbar
                'border absolute left-0 top-x0_5 max-h-[554px] w-full overflow-y-auto overflow-x-hidden rounded bg-legacy-noir shadow-[0px_4px_10px_0px_rgba(0,0,0,0.3)]',
                light
                  ? 'border-legacy-lightgray'
                  : 'border-[rgba(255,255,255,0.1)]'
              )}
            >
              {listing}
            </div>
          )}
        </div>
      </Popper>
    </div>
  );
};
